<template>
  <figure class="lynda-visual" :aria-label="chart.title">
    <figcaption>{{ chart.title }}</figcaption>
    <ul v-if="chart.variant !== 'pie'" class="lynda-legend">
      <li v-for="(series, s) in chart.series" :key="s">
        <i :style="{ background: palette[s % palette.length] }"></i
        >{{ series.name }}
      </li>
    </ul>
    <template v-if="chart.variant === 'bar'">
      <div
        v-for="(label, index) in chart.labels"
        :key="index"
        class="lynda-bar-group"
      >
        <div class="lynda-bar-label">{{ label }}</div>
        <button
          v-for="(series, s) in chart.series"
          :key="s"
          type="button"
          class="lynda-bar-row lynda-chart-target"
          :aria-label="`${label}: ${series.name}, ${format(series.values[index])}`"
          :aria-describedby="isActive(index, s) ? tooltipId : null"
          @mouseenter="showMark($event, index, s)"
          @mouseleave="scheduleHide"
          @focus="showMark($event, index, s)"
          @blur="hideTooltip"
          @click="showMark($event, index, s)"
        >
          <span class="lynda-bar-track">
            <i :style="{ left: `${(-min / (max - min)) * 100}%` }"></i
            ><span
              v-if="series.values[index] !== null"
              :style="barStyle(series.values[index], s)"
            ></span>
          </span>
          <span class="lynda-bar-value">{{
            format(series.values[index])
          }}</span>
        </button>
      </div>
      <p v-if="hasNegative" class="lynda-chart-note">
        Bars extend from zero; negative values extend to the left.
      </p>
    </template>
    <template v-else-if="chart.variant === 'pie'">
      <div v-for="(series, s) in chart.series" :key="s" class="lynda-pie-group">
        <strong v-if="chart.series.length > 1">{{ series.name }}</strong>
        <svg
          viewBox="0 0 200 200"
          role="group"
          :aria-label="`${chart.title}: ${series.name}`"
        >
          <path
            v-for="(slice, i) in pieSlices(series)"
            :key="i"
            :d="slice"
            :fill="palette[i % palette.length]"
            stroke="white"
            stroke-width="2"
            class="lynda-chart-target"
            role="button"
            :tabindex="series.values[i] > 0 ? 0 : -1"
            :aria-label="`${chart.labels[i]}: ${series.name}, ${format(series.values[i])}, ${pieShare(s, i)} of total`"
            :aria-describedby="isActive(i, s) ? tooltipId : null"
            @mouseenter="showMark($event, i, s)"
            @mouseleave="scheduleHide"
            @focus="showMark($event, i, s)"
            @blur="hideTooltip"
            @click="showMark($event, i, s)"
            @keydown.enter.prevent="showMark($event, i, s)"
            @keydown.space.prevent="showMark($event, i, s)"
          />
        </svg>
        <ul class="lynda-legend">
          <li v-for="(label, i) in chart.labels" :key="i">
            <i :style="{ background: palette[i % palette.length] }"></i
            >{{ label }} <strong>{{ format(series.values[i]) }}</strong>
          </li>
        </ul>
      </div>
    </template>
    <template v-else>
      <svg
        viewBox="0 0 480 215"
        role="group"
        tabindex="0"
        :aria-label="`${chart.title}. Use left and right arrow keys to inspect values.`"
        :aria-describedby="active ? tooltipId : null"
        class="lynda-line-chart"
        @mouseenter="dismissed = false"
        @mousemove="inspectPlot"
        @mouseleave="scheduleHide"
        @click="inspectPlot"
        @focus="showPlot($event.currentTarget, 0)"
        @blur="hideTooltip"
        @keydown.left.prevent="movePlot($event, -1)"
        @keydown.right.prevent="movePlot($event, 1)"
        @keydown.home.prevent="showPlot($event.currentTarget, 0)"
        @keydown.end.prevent="showPlot($event.currentTarget, chart.labels.length - 1)"
      >
        <line
          v-for="n in 5"
          :key="n"
          x1="45"
          x2="460"
          :y1="20 + (n - 1) * 45"
          :y2="20 + (n - 1) * 45"
          stroke="#e5eaf2"
        />
        <text
          v-for="n in 5"
          :key="`tick-${n}`"
          x="40"
          :y="24 + (n - 1) * 45"
          text-anchor="end"
        >
          {{ format(max - ((n - 1) * (max - min)) / 4) }}
        </text>
        <line
          v-if="active"
          :x1="x(active.index)"
          :x2="x(active.index)"
          y1="20"
          y2="200"
          stroke="#607086"
          stroke-dasharray="3 3"
          pointer-events="none"
        />
        <g v-for="(series, s) in chart.series" :key="s">
          <path
            v-if="chart.variant === 'area'"
            :d="path(series, true)"
            :fill="palette[s % palette.length]"
            opacity="0.12"
          />
          <path
            :d="path(series)"
            fill="none"
            :stroke="palette[s % palette.length]"
            stroke-width="3"
          />
          <g
            v-for="(value, i) in series.values"
            :key="i"
          >
            <circle
              v-if="value !== null"
              :cx="x(i)"
              :cy="y(value)"
              :r="active && active.index === i ? 6 : 4"
              :fill="palette[s % palette.length]"
            />
          </g>
        </g>
      </svg>
      <div class="lynda-axis-labels">
        <span>{{ chart.labels[0] }}</span>
        <span>{{ chart.labels[chart.labels.length - 1] }}</span>
      </div>
    </template>
    <p v-if="hasMissing" class="lynda-chart-note">
      Gaps indicate missing values, not zero. See the answer for observation coverage.
    </p>
    <details>
      <summary>View figures</summary>
      <div
        class="lynda-table-scroll"
        role="region"
        tabindex="0"
        :aria-label="`${chart.title} figures`"
      >
        <table>
          <thead>
            <tr>
              <th scope="col">Group</th>
              <th v-for="series in chart.series" :key="series.name" scope="col">
                {{ series.name }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(label, i) in chart.labels" :key="i">
              <th scope="row">{{ label }}</th>
              <td v-for="series in chart.series" :key="series.name">
                {{ format(series.values[i]) }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </details>
    <div
      v-if="active"
      :id="tooltipId"
      ref="tooltip"
      role="tooltip"
      aria-live="polite"
      class="lynda-chart-tooltip"
      :style="tooltipStyle"
      @mouseenter="keepTooltip"
      @mouseleave="scheduleHide"
    >
      <strong>{{ chart.labels[active.index] }}</strong>
      <div v-for="row in tooltipRows" :key="row.name" class="lynda-tooltip-row">
        <i :style="{ background: row.color }" aria-hidden="true"></i>
        <span>{{ row.name }}</span>
        <b>{{ row.value }}</b>
      </div>
      <p v-if="chart.variant === 'pie'">{{ pieShare(active.series, active.index) }} of displayed total</p>
    </div>
  </figure>
</template>
<script>
const palette = [
  '#326ee7',
  '#147d82',
  '#7653a9',
  '#996318',
  '#a34665',
  '#536b8c',
]
export default {
  name: 'AnswerChart',
  props: { chart: { type: Object, required: true } },
  data: () => ({
    palette,
    active: null,
    tooltipStyle: {},
    hideTimer: null,
    dismissed: false,
  }),
  computed: {
    tooltipId() {
      return `lynda-chart-tooltip-${this._uid}`
    },
    tooltipRows() {
      if (!this.active) return []
      return this.chart.series.flatMap((series, s) => {
        if (this.active.series !== null && this.active.series !== s) return []
        return [{
          name: series.name,
          value: this.format(series.values[this.active.index]),
          color: palette[(this.chart.variant === 'pie' ? this.active.index : s) % palette.length],
        }]
      })
    },
    values() {
      return this.chart.series.flatMap((s) => s.values).filter((n) => n !== null)
    },
    max() {
      return Math.max(1, ...this.values)
    },
    min() {
      return Math.min(0, ...this.values)
    },
    hasNegative() {
      return this.min < 0
    },
    hasMissing() {
      return this.chart.series.some((s) => s.values.includes(null))
    },
  },
  mounted() {
    document.addEventListener('pointerdown', this.hideOutside)
    document.addEventListener('keydown', this.dismissTooltip, true)
    window.addEventListener('scroll', this.hideTooltip, true)
    window.addEventListener('resize', this.hideTooltip)
  },
  beforeDestroy() {
    this.keepTooltip()
    document.removeEventListener('pointerdown', this.hideOutside)
    document.removeEventListener('keydown', this.dismissTooltip, true)
    window.removeEventListener('scroll', this.hideTooltip, true)
    window.removeEventListener('resize', this.hideTooltip)
  },
  methods: {
    isActive(index, series) {
      return this.active && this.active.index === index && this.active.series === series
    },
    keepTooltip() {
      clearTimeout(this.hideTimer)
    },
    scheduleHide() {
      this.keepTooltip()
      this.dismissed = false
      if (document.activeElement?.getAttribute('aria-describedby') === this.tooltipId) return
      this.hideTimer = setTimeout(this.hideTooltip, 150)
    },
    hideTooltip() {
      this.keepTooltip()
      this.active = null
    },
    hideOutside(event) {
      if (!this.$el.contains(event.target) || !event.target.closest('.lynda-chart-target, .lynda-line-chart, .lynda-chart-tooltip'))
        this.hideTooltip()
    },
    dismissTooltip(event) {
      if (event.key !== 'Escape' || !this.active) return
      event.stopPropagation()
      this.hideTooltip()
      this.dismissed = true
    },
    showMark(event, index, series) {
      const rect = event.currentTarget.getBoundingClientRect()
      this.showTooltip(index, series, rect.left + rect.width / 2, rect.bottom)
    },
    inspectPlot(event) {
      if (this.dismissed && event.type === 'mousemove') return
      const rect = event.currentTarget.getBoundingClientRect()
      const plotX = ((event.clientX - rect.left) / rect.width) * 480
      const index = Math.round(((plotX - 45) / 415) * (this.chart.labels.length - 1))
      this.showPlot(event.currentTarget, index)
    },
    movePlot(event, direction) {
      this.showPlot(event.currentTarget, (this.active ? this.active.index : 0) + direction)
    },
    showPlot(element, index) {
      index = Math.max(0, Math.min(this.chart.labels.length - 1, index))
      const rect = element.getBoundingClientRect()
      this.showTooltip(index, null, rect.left + this.x(index) * rect.width / 480, rect.top + 20)
    },
    showTooltip(index, series, left, top) {
      this.keepTooltip()
      if (this.isActive(index, series)) return
      this.dismissed = false
      this.active = { index, series }
      this.tooltipStyle = { left: `${left}px`, top: `${top + 8}px`, visibility: 'hidden' }
      this.$nextTick(() => {
        if (!this.active) return
        const tooltip = this.$refs.tooltip.getBoundingClientRect()
        const panel = this.$el.closest('.lynda-panel').getBoundingClientRect()
        const right = Math.min(panel.right, window.innerWidth) - 12
        const bottom = Math.min(panel.bottom, window.innerHeight) - 12
        this.tooltipStyle = {
          left: `${Math.max(panel.left + 12, Math.min(left, right - tooltip.width))}px`,
          top: `${Math.max(12, Math.min(top + 8, bottom - tooltip.height))}px`,
        }
      })
    },
    pieShare(series, index) {
      const values = this.chart.series[series].values
      const total = values.reduce((sum, value) => sum + value, 0)
      return `${new Intl.NumberFormat('en-GB', { maximumFractionDigits: 1 }).format(values[index] / total * 100)}%`
    },
    barStyle(value, series) {
      return {
        left: `${
          ((Math.min(value, 0) - this.min) / (this.max - this.min)) * 100
        }%`,
        width: `${(Math.abs(value) / (this.max - this.min)) * 100}%`,
        background: palette[series % palette.length],
      }
    },
    format(value) {
      if (value === null) return 'No data'
      const number = new Intl.NumberFormat('en-GB', {
        maximumFractionDigits: 2,
      }).format(value)
      return this.chart.unit === '%' ? `${number}%` : number
    },
    x(i) {
      return 45 + (i / Math.max(1, this.chart.labels.length - 1)) * 415
    },
    y(n) {
      return 200 - ((n - this.min) / (this.max - this.min)) * 180
    },
    path(series, area = false) {
      const segments = []
      let points = []
      const finish = () => {
        if (!points.length) return
        let d = points.map(([x, y], i) => `${i ? 'L' : 'M'}${x},${y}`).join(' ')
        if (area)
          d += ` L${points[points.length - 1][0]},${this.y(0)} L${points[0][0]},${this.y(0)} Z`
        segments.push(d)
        points = []
      }
      series.values.forEach((n, i) => {
        if (n === null) finish()
        else points.push([this.x(i), this.y(n)])
      })
      finish()
      return segments.join(' ')
    },
    pieSlices(series) {
      const total = series.values.reduce((a, b) => a + b, 0)
      let angle = -Math.PI / 2
      return series.values.map((value) => {
        const next = angle + (value / total) * Math.PI * 2
        const start = [100 + 90 * Math.cos(angle), 100 + 90 * Math.sin(angle)]
        const end = [100 + 90 * Math.cos(next), 100 + 90 * Math.sin(next)]
        const d =
          value === total
            ? 'M100,10 A90,90 0 1,1 100,190 A90,90 0 1,1 100,10 Z'
            : `M100,100 L${start} A90,90 0 ${
                value / total > 0.5 ? 1 : 0
              },1 ${end} Z`
        angle = next
        return d
      })
    },
  },
}
</script>
